import { api } from './api'
import { readCachedCatalog, writeCachedCatalog } from './catalogCache'
import { USE_FIXTURES } from './env'
import type { BookDetails, BookStats, Catalog } from './types'

/**
 * Fetches the slim catalogue, preferring the cached copy and revalidating it
 * with `?version=` + `If-None-Match` (§15.3 steps 1–3).
 */
export async function loadCatalog(): Promise<Catalog> {
  if (USE_FIXTURES) {
    const { devCatalog } = await import('./devData')
    return devCatalog()
  }

  const cached = readCachedCatalog()
  const response = await api.catalog(cached?.version, cached?.etag ?? null)

  if (response.notModified && cached) return cached.payload

  writeCachedCatalog(response.data, response.etag)
  return response.data
}

/**
 * The detail-only half of each record. Fetched after the catalogue rather than
 * inside it: together they exceed the §15.5 first-paint budget, and nothing on
 * a list screen needs this half (see tools/build-dev-catalog.mjs).
 */
export async function loadBookDetails(): Promise<Record<string, BookDetails>> {
  if (USE_FIXTURES) {
    const { devBookDetails } = await import('./devData')
    return devBookDetails()
  }
  const response = await api.bookDetails()
  return response.data
}

/** Mutable counters live outside the catalogue and refresh every 60 s (§15.3). */
export async function loadStats(ids?: string[]): Promise<BookStats[]> {
  if (USE_FIXTURES) {
    const { devStats } = await import('./devData')
    return devStats(ids)
  }
  const response = await api.stats(ids)
  return response.data
}

export function statsToMap(stats: BookStats[]): Map<string, BookStats> {
  return new Map(stats.map((s) => [s.bookId, s]))
}
